<template>
  <div>
    <div v-if="mediaFile" class="sweeper-container">
      <img
        ref="imageEl"
        class="sweeper-image"
        :src="mediaFile"
        alt=""
        @load="onImageLoad"
      />

      <div v-if="!isCompleted" class="tiles-layer" ref="tilesLayer">
        <button
          v-for="(tile, idx) in tiles"
          :key="idx"
          type="button"
          class="tile"
          :style="{
            left: tile.x + 'px',
            top: tile.y + 'px',
            width: tile.width + 'px',
            height: tile.height + 'px',
            opacity: draggingIndex === idx ? 0.85 : 1
          }"
          @mousedown.prevent="onPointerDown($event, idx)"
          @touchstart.prevent="onTouchStart($event, idx)"
          @touchmove.prevent="onTouchMove($event)"
          @touchend.prevent="onPointerUp($event)"
          :aria-label="'cover ' + (idx + 1)"
        >
          <img v-if="coverFile" :src="coverFile" alt="" />
        </button>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'GameImageSweeper',
  props: {
    block: {
      type: Object,
      required: true
    },
    isCompleted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mediaFile: null,
      coverFile: null,
      coverAmount: 6,
      tiles: [],
      draggingIndex: null,
      dragOffsetX: 0,
      dragOffsetY: 0,
      containerRect: null,
      coverNaturalWidth: 100,
      coverNaturalHeight: 100
    }
  },
  watch: {
    block: {
      handler(newBlock) {
        if (newBlock?.data?.media) {
          this.mediaFile = newBlock.data.media.preview_url
        }
        // Only load cover image if not completed
        if (!this.isCompleted && newBlock?.data?.cover_image) {
          this.coverFile = newBlock.data.cover_image.preview_url
          this.$nextTick(() => this.measureCoverIntrinsic())
        } else if (this.isCompleted) {
          // If completed, don't load cover image
          this.coverFile = null
          this.tiles = []
        }
        if (Number.isInteger(newBlock?.data?.cover_amount)) {
          this.coverAmount = Math.max(1, parseInt(newBlock.data.cover_amount, 10))
        }
        // Only initialize tiles if not completed
        if (!this.isCompleted) {
          this.tiles = []
          this.$nextTick(() => this.initializeTiles())
        }
      },
      immediate: true
    },
    isCompleted: {
      handler(newValue) {
        if (newValue) {
          // When marked as completed, clear tiles and cover file
          this.tiles = []
          this.coverFile = null
        } else {
          // When not completed, reload cover image and initialize tiles
          if (this.block?.data?.cover_image) {
            this.coverFile = this.block.data.cover_image.preview_url
            this.$nextTick(() => {
              this.measureCoverIntrinsic()
              this.initializeTiles()
            })
          }
        }
      },
      immediate: true
    }
  },
  mounted() {
    window.addEventListener('mousemove', this.onPointerMove, { passive: true })
    window.addEventListener('mouseup', this.onPointerUp, { passive: true })
    window.addEventListener('touchstart', this.onTouchStart, { passive: false })
    window.addEventListener('touchmove', this.onTouchMove, { passive: false })
    window.addEventListener('touchend', this.onPointerUp, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onPointerMove)
    window.removeEventListener('mouseup', this.onPointerUp)
    window.removeEventListener('touchstart', this.onTouchStart)
    window.removeEventListener('touchmove', this.onTouchMove)
    window.removeEventListener('touchend', this.onPointerUp)
  },
  methods: {
    onImageLoad() {
      this.cacheContainerRect()
      // Only initialize tiles if not completed
      if (!this.isCompleted) {
        this.initializeTiles()
      }
    },
    cacheContainerRect() {
      const img = this.$refs.imageEl
      if (!img) return
      const rect = img.getBoundingClientRect()
      this.containerRect = {
        left: rect.left + window.scrollX,
        top: rect.top + window.scrollY,
        width: rect.width,
        height: rect.height
      }
    },
    measureCoverIntrinsic() {
      // Don't load cover if already completed
      if (this.isCompleted || !this.coverFile) return
      const img = new Image()
      img.onload = () => {
        this.coverNaturalWidth = img.naturalWidth || 100
        this.coverNaturalHeight = img.naturalHeight || 100
        // Only initialize tiles if not completed
        if (!this.isCompleted) {
          this.initializeTiles()
        }
      }
      img.src = this.coverFile
    },
    initializeTiles() {
      // Don't initialize if already completed
      if (this.isCompleted) return
      if (!this.mediaFile || !this.coverFile) return
      this.cacheContainerRect()
      if (!this.containerRect) return
      const { width: cw, height: ch } = this.containerRect
      if (cw <= 0 || ch <= 0) return
      const targetWidth = Math.max(40, Math.min(cw * 0.20, this.coverNaturalWidth))
      const scale = targetWidth / this.coverNaturalWidth
      const targetHeight = Math.max(40, this.coverNaturalHeight * scale)
      const tiles = []
      for (let i = 0; i < this.coverAmount; i++) {
        const { x, y } = this.randomInside(cw - targetWidth, ch - targetHeight)
        tiles.push({ x, y, width: targetWidth, height: targetHeight })
      }
      this.tiles = tiles
    },
    randomInside(maxX, maxY) {
      const x = Math.floor(Math.random() * Math.max(1, maxX))
      const y = Math.floor(Math.random() * Math.max(1, maxY))
      return { x, y }
    },
    onPointerDown(e, idx) {
      // Don't allow interaction if already completed
      if (this.isCompleted) return
      this.cacheContainerRect()
      const pt = this.pagePoint(e)
      const tile = this.tiles[idx]
      if (!tile || !this.containerRect) return
      const tilePageLeft = this.containerRect.left + tile.x
      const tilePageTop = this.containerRect.top + tile.y
      this.draggingIndex = idx
      this.dragOffsetX = pt.x - tilePageLeft
      this.dragOffsetY = pt.y - tilePageTop
    },
    onPointerMove(e) {
      // Don't allow interaction if already completed
      if (this.isCompleted || this.draggingIndex === null) return
      if (!this.containerRect) this.cacheContainerRect()
      const pt = this.pagePoint(e)
      const idx = this.draggingIndex
      const tile = this.tiles[idx]
      if (!tile || !this.containerRect) return
      const newX = pt.x - this.containerRect.left - this.dragOffsetX
      const newY = pt.y - this.containerRect.top - this.dragOffsetY
      // Vue 3: use splice to keep array reactive when replacing item
      this.tiles.splice(idx, 1, {
        ...tile,
        x: newX,
        y: newY
      })
    },
    onPointerUp() {
      if (this.draggingIndex === null) return
      this.draggingIndex = null
      this.checkCompletion()
    },
    onTouchStart(e, idx) {
      // Don't allow interaction if already completed
      if (this.isCompleted) return
      // Handle both direct tile touch and window touch events
      if (typeof idx === 'number') {
        // Direct tile touch
        if (!e.touches || e.touches.length === 0) return
        e.preventDefault()
        this.onPointerDown(e.touches[0], idx)
      } else {
        // Window touch event - check if it's on a tile
        if (!e.touches || e.touches.length === 0) return
        const touch = e.touches[0]
        const target = document.elementFromPoint(touch.clientX, touch.clientY)
        if (target && target.classList.contains('tile')) {
          const tileIndex = Array.from(target.parentNode.children).indexOf(target)
          if (tileIndex >= 0) {
            e.preventDefault()
            this.onPointerDown(touch, tileIndex)
          }
        }
      }
    },
    onTouchMove(e) {
      if (!e.touches || e.touches.length === 0) return
      e.preventDefault()
      this.onPointerMove(e.touches[0])
    },
    pagePoint(e) {
      return { x: e.pageX !== undefined ? e.pageX : e.clientX + window.scrollX, y: e.pageY !== undefined ? e.pageY : e.clientY + window.scrollY }
    },
    checkCompletion() {
      // Don't check if already completed (from prop)
      if (this.isCompleted || !this.containerRect || this.tiles.length === 0) return
      const left = 0
      const top = 0
      const right = this.containerRect.width
      const bottom = this.containerRect.height
      const allOutside = this.tiles.every(t => {
        const centerX = t.x + t.width / 2
        const centerY = t.y + t.height / 2
        return centerX < left || centerX > right || centerY < top || centerY > bottom
      })
      if (allOutside) {
        this.$emit('complete')
      }
    }
  }
}
</script> 

<style scoped>
.sweeper-container {
  position: relative;
  display: inline-block;
}

.sweeper-image {
  display: block;
  max-width: 100%;
  height: auto;
}

.tiles-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
  overflow: hidden; /* hide tiles when moved outside the image */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.tile {
  position: absolute;
  padding: 0;
  border: none;
  background: transparent;
  cursor: grab;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.tile img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

</style>